import { makeAutoObservable } from 'mobx';
import { GraphStructure, createScopesGraph } from './scopesGraph';
import { NAME_SUFFIX, SCOPE_GROUPS_LIST } from './scopesGraphUtils';
import { ScopesListProps } from './types';

type ScopesListStoreProps = ScopesListProps;

export function createScopesListStore(props: ScopesListStoreProps) {
  return new ScopesListStore(props);
}

export class ScopesListStore {
  existingScopes: ScopesListProps['existingScopes'];

  scopeDeps: ScopesListProps['scopeDeps'];

  endpointDeps: ScopesListProps['endpointDeps'];

  graph: GraphStructure;

  checkedScopes: string[];

  checkedScopeGroups: Set<string>;

  constructor(props: ScopesListStoreProps) {
    makeAutoObservable(this);

    this.existingScopes = props.existingScopes;
    this.scopeDeps = props.scopeDeps;
    this.endpointDeps = props.endpointDeps;
    this.graph = createScopesGraph(this.scopeDeps, this.endpointDeps);
    this.checkedScopes = props.existingScopes
      ? props.existingScopes.includes('All')
        ? this.listAllScopesPossible
        : this.graph.depthFirstSearch(props.existingScopes)
      : this.listAllScopesPossible;
    this.checkedScopeGroups = new Set(this.checkedScopes.includes('All') ? ['All'] : []);
  }

  get listAllScopesPossible() {
    return this.graph.depthFirstSearch(SCOPE_GROUPS_LIST, false);
  }

  get checkedScopesWithoutScopeGroups() {
    // Filter out the scope group names and 'All' string, to only keep the actually selected scopes
    const filteredScopes = this.checkedScopes.filter((se) => !se.includes('_TOP') && se !== 'All');

    return filteredScopes;
  }

  get isAllScopesSelected() {
    const allScopesSelected = this.checkedScopes.length === this.listAllScopesPossible.length;
    return allScopesSelected;
  }

  toggleSelectAll(selectAllScopes: boolean) {
    const newCheckedScopes = selectAllScopes ? this.listAllScopesPossible : [];
    const updatedScopeGroups = newCheckedScopes ? ['All'] : [];

    this.checkedScopes = newCheckedScopes;
    this.checkedScopeGroups = new Set(updatedScopeGroups);
  }

  handleCheckboxClick(scopeName: string) {
    const updatedScopeGroupsSet = this.checkedScopeGroups.has('All') ? new Set([]) : this.checkedScopeGroups;

    // Toggle the entire scope group
    if (scopeName.includes(NAME_SUFFIX)) {
      if (!updatedScopeGroupsSet.has(scopeName)) {
        updatedScopeGroupsSet.add(scopeName);
      } else {
        updatedScopeGroupsSet.delete(scopeName);
      }

      this.checkedScopeGroups = updatedScopeGroupsSet;
      this.checkedScopes = this.graph.depthFirstSearch(Array.from(updatedScopeGroupsSet), false);
    }
    // Unselect the scope
    else if (this.checkedScopes.includes(scopeName)) {
      const filterList = [scopeName, 'All', ...this.graph.depthFirstSearch([scopeName], false)];
      this.checkedScopes = this.checkedScopes.filter((scope) => !filterList.includes(scope));
    }
    // Select the scope
    else {
      const selectionList = [scopeName, ...this.graph.depthFirstSearch([scopeName], false)];
      this.checkedScopes = [...Array.from(new Set<string>([...this.checkedScopes, ...selectionList]))].filter((scope) => scope !== 'All');
    }
  }
}
